import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  AlarmClock,
  AlertTriangle,
  CalendarCheck2,
  Compass,
  FileCheck,
  LogOut,
  Monitor,
  QrCode,
  ShieldCheck,
  type LucideIcon,
} from 'lucide-react';

import { Button } from '@/shared/ui/button';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '@/shared/ui/drawer';
import { Separator } from '@/shared/ui/separator';
import { cn } from '@/shared/lib/utils';

import { FieldRoleSwitcher } from './field-role-switcher';
import { useFieldThemeClass } from '../../theme';

interface MenuItem {
  id: string;
  label: string;
  icon: LucideIcon;
  route?: string;
  disabled?: boolean;
}

const MODULE_ITEMS: MenuItem[] = [
  { id: 'timesheet', label: 'Timesheet', icon: AlarmClock, route: '/field/time-tracking' },
  { id: 'activities', label: 'Activities', icon: CalendarCheck2, route: '/field/activities', disabled: true },
  { id: 'qr-scan', label: 'QR Scan', icon: QrCode, disabled: true },
  { id: 'handover', label: 'Handover', icon: FileCheck, disabled: true },
  { id: 'safety', label: 'Safety Check', icon: ShieldCheck, disabled: true },
  { id: 'issues', label: 'Report Issue', icon: AlertTriangle, disabled: true },
];

interface FieldMenuDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Module switcher / "More" drawer that swipes up from the bottom of the screen.
 * Each row is 56px tall to meet 44px+ touch targets.
 */
export function FieldMenuDrawer({ open, onOpenChange }: FieldMenuDrawerProps) {
  const navigate = useNavigate();
  const themeCls = useFieldThemeClass();

  function handleNav(item: MenuItem) {
    if (item.disabled || !item.route) {
      toast.info(`${item.label} — coming soon`);
      return;
    }
    onOpenChange(false);
    navigate(item.route);
  }

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className={cn('field-surface tw-pb-6', themeCls)}>
        <DrawerHeader className="tw-pb-2">
          <DrawerTitle className="tw-flex tw-items-center tw-gap-2 tw-text-base">
            <Compass className="tw-h-4 tw-w-4 tw-text-primary" />
            Modules
          </DrawerTitle>
        </DrawerHeader>

        <div className="tw-px-3 tw-pb-3">
          <FieldRoleSwitcher onChange={() => onOpenChange(false)} />
        </div>

        <div className="tw-flex tw-flex-col tw-gap-1 tw-px-3">
          {MODULE_ITEMS.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => handleNav(item)}
                className={cn(
                  'tw-flex tw-h-14 tw-items-center tw-gap-3 tw-rounded-lg tw-px-3 tw-text-left tw-text-sm tw-font-medium tw-transition-colors',
                  'hover:tw-bg-accent hover:tw-text-accent-foreground',
                  item.disabled && 'tw-text-muted-foreground',
                )}
              >
                <Icon className="tw-h-5 tw-w-5 tw-text-primary" />
                <span className="tw-flex-1">{item.label}</span>
                {item.disabled ? (
                  <span className="tw-rounded-full tw-bg-muted tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-semibold tw-uppercase tw-text-muted-foreground">
                    Soon
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>

        <Separator className="tw-my-3" />

        <div className="tw-flex tw-flex-col tw-gap-2 tw-px-3">
          <Button
            variant="outline"
            size="lg"
            className="tw-h-12 tw-w-full tw-justify-start"
            onClick={() => {
              onOpenChange(false);
              navigate('/dashboard');
            }}
          >
            <Monitor className="tw-mr-2 tw-h-4 tw-w-4" />
            Switch to Desktop
          </Button>
          <Button
            variant="ghost"
            size="lg"
            className="tw-h-12 tw-w-full tw-justify-start tw-text-destructive hover:tw-bg-destructive/10 hover:tw-text-destructive"
            onClick={() => {
              onOpenChange(false);
              navigate('/logout');
            }}
          >
            <LogOut className="tw-mr-2 tw-h-4 tw-w-4" />
            Sign Out
          </Button>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
